import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { createSocialMediaLink } from '@/features/workspace/workspaceThunks';
import { InputLabel } from '@/components/ui/InputLabel';
import { socialMediaOptions } from './SocialMediaSection';
import { useWebinarT } from '@/hooks/useWebinarT';

type FormValues = {
  socialMedia: string;
  url: string;
};

// Domaines autorisés pour chaque réseau social (correspond au backend)
const MEDIA_DOMAINS: Record<string, string[]> = {
  FACEBOOK: ['facebook.com', 'fb.com'],
  INSTAGRAM: ['instagram.com'],
  TIKTOK: ['tiktok.com'],
  X: ['x.com', 'twitter.com'],
  GOOGLE: ['google.com', 'g.page', 'business.site', 'goo.gl'],
  PINTEREST: ['pinterest.com'],
  LINKEDIN: ['linkedin.com', 'lnkd.in'],
  SNAPCHAT: ['snapchat.com'],
  YOUTUBE: ['youtube.com', 'youtu.be'],
  TWITCH: ['twitch.tv'],
  WHATSAPP: ['whatsapp.com', 'wa.me'],
};

// Fonction pour vérifier si le domaine de l'URL correspond au réseau social
const hostMatchesMedia = (url: string, socialMedia: string): boolean => {
  try {
    const urlObj = new URL(url);
    const host = urlObj.hostname.toLowerCase();
    const allowedDomains = MEDIA_DOMAINS[socialMedia] || [];

    return allowedDomains.some((domain) => {
      const domainLower = domain.toLowerCase();
      return host === domainLower || host.endsWith('.' + domainLower);
    });
  } catch {
    return false;
  }
};

export default function DialogAddSocial() {
  const [open, setOpen] = useState(false);
  const { t } = useWebinarT('webinars');
  const dispatch = useAppDispatch();
  const currentWorkspace = useAppSelector((state: RootState) => state.workspace.currentWorkspace);
  const socialMediaLoading = useAppSelector(
    (state: RootState) => state.workspace.socialMediaLoading,
  );
  const socialMediaError = useAppSelector((state: RootState) => state.workspace.socialMediaError);

  const schema = useMemo(
    () =>
      z
        .object({
          socialMedia: z.string().min(1, t('socialMedia.errors.selectNetwork')),
          url: z
            .string()
            .min(1, t('socialMedia.errors.enterLink'))
            .url(t('socialMedia.errors.invalidUrl'))
            .refine(
              (url) => {
                try {
                  const urlObj = new URL(url);
                  const scheme = urlObj.protocol;
                  return scheme === 'http:' || scheme === 'https:';
                } catch {
                  return false;
                }
              },
              {
                message: t('socialMedia.errors.badScheme'),
              },
            ),
        })
        .superRefine((data, ctx) => {
          if (data.socialMedia && data.url) {
            if (!hostMatchesMedia(data.url, data.socialMedia)) {
              const allowedDomains = MEDIA_DOMAINS[data.socialMedia] || [];
              const domainsText = allowedDomains.join(', ');
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('socialMedia.errors.domainMismatch', { domains: domainsText }),
                path: ['url'],
              });
            }
          }
        }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      socialMedia: '',
      url: '',
    },
    mode: 'onChange',
  });

  const onSubmit = async (data: FormValues) => {
    if (!currentWorkspace?.id) {
      console.error('Aucun workspace sélectionné');
      return;
    }

    const resultAction = await dispatch(
      createSocialMediaLink({
        workspaceId: currentWorkspace.id,
        input: {
          url: data.url,
          socialMedia: data.socialMedia,
        },
      }),
    );

    if (createSocialMediaLink.fulfilled.match(resultAction)) {
      form.reset();
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div className="flex items-center gap-2 cursor-pointer">
          <Plus />
          <Label className="cursor-pointer">{t('socialMedia.actions.add')}</Label>
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-[1053px]">
        <DialogHeader className="pb-8">
          <DialogTitle>{t('socialMedia.dialog.title')}</DialogTitle>
          <DialogDescription>{t('socialMedia.dialog.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1">
            <Controller
              name="socialMedia"
              control={form.control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger label={t('socialMedia.form.networkLabel')}>
                    <SelectValue placeholder={t('socialMedia.form.networkPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {socialMediaOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2 font-medium">
                          {option.icon} {option.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {form.formState.errors.socialMedia && (
              <p className="text-xs text-error">{form.formState.errors.socialMedia.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Controller
              name="url"
              control={form.control}
              render={({ field }) => (
                <InputLabel
                  label={t('socialMedia.form.urlLabel')}
                  id="link"
                  type="text"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
            {form.formState.errors.url && (
              <p className="text-xs text-error">{form.formState.errors.url.message}</p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={form.formState.isSubmitting || socialMediaLoading}
          >
            {form.formState.isSubmitting
              ? t('socialMedia.button.submitting')
              : t('socialMedia.button.submit')}
          </Button>
          {socialMediaError && <p className="text-xs text-error text-center">{socialMediaError}</p>}
        </form>
      </DialogContent>
    </Dialog>
  );
}
